export const DNS_RECORD_TYPES = ["A", "AAAA", "CNAME", "MX", "TXT", "SRV", "CAA", "NS"] as const;
export type DnsRecordType = (typeof DNS_RECORD_TYPES)[number];

export type DnsZoneStatus = "PENDING_NAMESERVERS" | "ACTIVE" | "ERROR";

export interface DnsProviderInfo {
  id: string;
  label: string;
  /** Whether the customer must supply a credential (API token) to connect a zone. */
  requiresCredential: boolean;
  credentialLabel: string;
  /** One-line help on where to get the credential and what it must be allowed to do. */
  credentialHelp: string;
  available: boolean;
  notes: string[];
}

export interface DnsRecord {
  /** The provider's id for this record (used for update/delete). */
  id: string;
  type: DnsRecordType;
  /** Fully-qualified name, e.g. "www.example.com" or the zone apex "example.com". */
  name: string;
  content: string;
  ttl: number;
  priority: number | null;
  /** Cloudflare proxying (orange cloud). Null for providers/records where it does not apply. */
  proxied: boolean | null;
  /** True for records the provider manages itself and the customer cannot edit (e.g. apex NS). */
  locked: boolean;
  /** HostDock DNS: created automatically for a domain, website or mailbox domain (and kept up to date until edited). */
  auto?: boolean;
}

export interface DnsZoneRecord {
  id: string;
  domain: string;
  provider: string;
  status: DnsZoneStatus;
  statusReason: string | null;
  nameservers: string[];
  recordCount: number | null;
  lastCheckedAt: string | null;
  createdAt: string;
  /** HostDock answers for this zone itself (provider "hostdock"); records are edited here, not at another provider. */
  hosted: boolean;
  /**
   * HostDock DNS only: whether HostDock's nameservers are answering right now. Until they are, `nameservers` is empty
   * and the customer must keep their current DNS provider (the records here are kept and published once they are).
   */
  dnsOnline: boolean;
}

export interface DnsZoneDetail extends DnsZoneRecord {
  /** How the customer makes the zone live: point these nameservers at their registrar. */
  nameserverInstructions: string | null;
}

/** One HostDock nameserver: a hostname and the node that answers on it. */
export interface DnsNameserver {
  hostname: string;
  serverId: string;
  serverName: string | null;
  /** The address the hostname must resolve to (its glue/A record). */
  ip: string | null;
  sync: { status: "OK" | "FAILED" | "PENDING"; at: string | null; error: string | null; zones: number | null };
}

/** How customer zones are published: one HostDock cluster, each server's own nameservers, or ClouDNS. */
export type DnsMode = "cluster" | "per_server" | "cloudns";

export type DnsSyncState = { status: "OK" | "FAILED" | "PENDING"; at: string | null; error: string | null; zones: number | null };

export interface DnsNameserverSettings {
  mode: DnsMode;
  /** Cluster: the nameservers in use, the admin's own (custom) or, automatically, qualifying servers' hostnames. */
  nameservers: DnsNameserver[];
  custom: boolean;
  /** Per server: every server with its own nameserver hostnames (empty: none set). */
  perServer: { serverId: string; serverName: string; ip: string; hostnames: string[]; dnsReady: boolean; sync: DnsSyncState }[];
  /** ClouDNS: the account (the password is never returned) and the last push. */
  cloudns: { authType: "auth-id" | "sub-auth-id" | "sub-auth-user"; authId: string; passwordSet: boolean; nameservers: string[]; sync: DnsSyncState } | null;
  /** Servers that can be nameservers (agent set up for DNS). */
  servers: { id: string; name: string; hostname: string; publicIp: string; dnsReady: boolean }[];
}
